import { Form } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiData } from 'types/leiTypes';

import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import { Context } from 'components/hoc/Store';
import FormWrapper from 'components/molecules/FormWrapper';

import { ETypZadostiLei } from 'enums/lei/leiDataEnums';
import { ETypAdresy } from 'enums/lei/typAdresy';

import { leiJazykySelect } from 'data/codebooks/lei/jazykySelect';
import { leiCountryRegionSelect } from 'data/codebooks/lei/leiCountryRegion';
import {
    leiCountrySelect,
    leiCountrySelectFondy,
    leiCountrySelectInternational
} from 'data/codebooks/lei/leiCountrySelect';
import { leiElfKodyPermanent, leiElfKodySelect } from 'data/codebooks/lei/leiElfKodySelect';
import { leiRejstrikyPermanent, leiRejstrikySelect } from 'data/codebooks/lei/leiRejstrikySelect';
import { leiTypNazvuSelect } from 'data/codebooks/lei/leiTypNazvuSelect';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';

import { IRejstrikErrs } from '../UpravaLei';

interface IDetailInfo {
    formDataSpolecnost?: ILeiData['spolecnost'];
    typZadosti: ETypZadostiLei | undefined;
    rejstrikErrs?: IRejstrikErrs;
}

export const getCountryText = (country: string, lang: string) => {
    const countryArr = country ? leiCountrySelect.filter(e => e.value === country) : [];
    return countryArr.length ? countryArr[0][lang === 'cz' ? 'labelcz' : 'labelen'] : '';
};
const getRegionText = (country: string, region: string, lang: string) => {
    const regArr =
        country && region
            ? (_getKeyValue_(country)(leiCountryRegionSelect) || []).filter(
                  (e: { value: string; labelcz: string; labelen: string }) => e.value.includes(region)
              )
            : [];
    return regArr.length ? regArr[0][lang === 'cz' ? 'labelcz' : 'labelen'] : '';
};

export const getPrehledFields = (
    dataSpolecnost: ILeiData['spolecnost'],
    lang: string,
    zadostTyp?: ETypZadostiLei,
    rejstrikErrs?: IRejstrikErrs
) => {
    const DetailAdresa =
        dataSpolecnost?.adresy && dataSpolecnost.adresy.filter(e => e.typadresy === ETypAdresy.LEGALADDRESS);
    const altLangValues =
        dataSpolecnost?.adresy && dataSpolecnost.adresy.filter(e => e.typadresy === ETypAdresy.LEGALADDRESSALTERNATIVE);
    const hqLangValues =
        dataSpolecnost?.adresy && dataSpolecnost.adresy.filter(e => e.typadresy === ETypAdresy.HEADQUARTERSADDRES);
    const altHqLangValues =
        dataSpolecnost?.adresy && dataSpolecnost.adresy.filter(e => e.typadresy === ETypAdresy.HQTRANSALTERNATIVE);
    return {
        VZNIKSPOLECNOSTI: (
            <>
                <DatePickerComponent
                    notRequired={true}
                    disabled={true}
                    label={zadostTyp === ETypZadostiLei.FUND ? 'LEI_DETAIL_DATUM_SPOL_FUND' : 'LEI_DETAIL_DATUM_SPOL'}
                    infoTooltipText="LEI_DETAIL_DATUM_SPOL_TOOLTIP"
                    name="entitycreationddatedetail"
                    initialValue={
                        dataSpolecnost?.entitycreationddate ? dayjs(dataSpolecnost.entitycreationddate) : undefined
                    }
                />
            </>
        ),
        LEGAL_NAME: (
            <>
                <InputComponent
                    notRequired={true}
                    disabled={true}
                    label={'LEI_DETAIL_PRAV_SPOL'}
                    name="legalname1"
                    extramessage={rejstrikErrs?.legalname ? 'LEI_NESHODNE_UDAJE' : ''}
                    initialValue={dataSpolecnost?.legalname}
                    maxLength={100}
                />
            </>
        ),
        OTHER_NAMES: (
            <>
                {dataSpolecnost?.otherentitynames?.map((e, i) => (
                    <div key={i}>
                        <InputComponent
                            label="LEI_DETAIL_JINY"
                            name={`otherentityname${i}a`}
                            initialValue={e.otherentityname}
                            maxLength={100}
                            notRequired={true}
                            disabled={true}
                        />
                        {e.otherentityname && (
                            <SelectComponent
                                name={`entitynametype${i}a`}
                                labelParagraph="LEI_DETAIL_NAZEV_TYP"
                                isFormItem={true}
                                data={leiTypNazvuSelect}
                                notRequired={true}
                                defaultValue={e.entitynametype}
                                disabled={true}
                            />
                        )}
                        {e.otherentityname && (
                            <SelectComponent
                                name={`lang${i}a`}
                                labelParagraph="LEI_DETAIL_NAZEV_JAZYK"
                                isFormItem={true}
                                data={leiJazykySelect}
                                defaultValue={e.lang}
                                disabled={true}
                                notRequired={true}
                            />
                        )}
                    </div>
                ))}
            </>
        ),
        LEGAL_FORM: (
            <>
                <SelectComponent
                    notRequired={true}
                    disabled={true}
                    name="entitylegalformcodetail1"
                    labelParagraph="LEI_DETAIL_PRAVFORMA"
                    isFormItem={true}
                    showOptionValue={true}
                    optionValueFilter={['8888']}
                    extramessage={rejstrikErrs?.entitylegalformcode ? 'LEI_NESHODNE_UDAJE' : ''}
                    data={[
                        ...leiElfKodyPermanent,
                        ...(DetailAdresa &&
                        DetailAdresa[0].country &&
                        Object.keys(leiElfKodySelect).includes(DetailAdresa[0].country)
                            ? _getKeyValue_(DetailAdresa[0].country)(leiElfKodySelect)
                            : [])
                    ]}
                    defaultValue={dataSpolecnost?.entitylegalformcode}
                />
                {dataSpolecnost?.otherlegalform && (
                    <InputComponent
                        notRequired={true}
                        disabled={true}
                        label="LEI_DETAIL_PRAV_NENALEZENO"
                        name="otherlegalformdetail"
                        initialValue={dataSpolecnost?.otherlegalform}
                        maxLength={100}
                    />
                )}
            </>
        ),
        OSTATNI: <></>,
        LEGAL_ADDRESS: (
            <>
                <InputComponent
                    notRequired={true}
                    disabled={true}
                    label={
                        window.location.hash.includes('lei-uprava')
                            ? 'LEI_DETAIL_ADRESA_TITLE_UPDATE'
                            : 'LEI_DETAIL_ADRESA_TITLE'
                    }
                    name="firstaddresslinedetail1"
                    extramessage={rejstrikErrs?.legaladdress ? 'LEI_NESHODNE_UDAJE' : ''}
                    initialValue={`${
                        DetailAdresa && DetailAdresa[0].firstaddressline && DetailAdresa[0].firstaddressline
                    }, ${DetailAdresa && DetailAdresa[0].city}, ${DetailAdresa && DetailAdresa[0].postalcode}, ${
                        DetailAdresa && getCountryText(DetailAdresa[0].country || '', lang)
                    } ${
                        DetailAdresa && !!DetailAdresa[0].region
                            ? ', ' + getRegionText(DetailAdresa[0].country || '', DetailAdresa[0].region || '', lang)
                            : ''
                    }`}
                    maxLength={100}
                />
            </>
        ),
        HQ_ADDRESS: (
            <>
                {hqLangValues?.length ? (
                    <InputComponent
                        notRequired={true}
                        disabled={true}
                        label={
                            window.location.hash.includes('lei-uprava')
                                ? zadostTyp === ETypZadostiLei.FUND
                                    ? 'LEI_DETAIL_ADRESA_HQ_FUND_UPDATE'
                                    : 'LEI_DETAIL_ADRESA_HQ_UPDATE'
                                : zadostTyp === ETypZadostiLei.FUND
                                  ? 'LEI_DETAIL_ADRESA_HQ_FUND'
                                  : 'LEI_DETAIL_ADRESA_HQ'
                        }
                        name="hqlangfirstaddresslineP"
                        initialValue={`${
                            hqLangValues && hqLangValues[0].firstaddressline && hqLangValues[0].firstaddressline
                        }, ${
                            hqLangValues &&
                            (hqLangValues[0].additionaladdressline ? hqLangValues[0].additionaladdressline + ', ' : '')
                        }${hqLangValues && hqLangValues[0].city}, ${hqLangValues && hqLangValues[0].postalcode}, ${
                            hqLangValues && getCountryText(hqLangValues[0].country || '', lang)
                        } ${
                            hqLangValues && !!hqLangValues[0].region
                                ? ', ' +
                                  getRegionText(hqLangValues[0].country || '', hqLangValues[0].region || '', lang)
                                : ''
                        }`}
                        maxLength={100}
                    />
                ) : null}
            </>
        ),
        LEGAL_ADDRESS_ALT: (
            <>
                {altLangValues?.length ? (
                    <InputComponent
                        notRequired={true}
                        disabled={true}
                        label="LEI_DETAIL_ADRESA_TITLE_VARIANTA"
                        name="altlangfirstaddresslineP"
                        initialValue={`${altLangValues && altLangValues[0].firstaddressline}, ${
                            altLangValues && altLangValues[0].city
                        }, ${altLangValues && altLangValues[0].postalcode}, ${
                            altLangValues && getCountryText(altLangValues[0].country || '', lang)
                        } ${
                            altLangValues && !!altLangValues[0].region
                                ? ', ' +
                                  getRegionText(altLangValues[0].country || '', altLangValues[0].region || '', lang)
                                : ''
                        }`}
                        maxLength={100}
                    />
                ) : null}
            </>
        ),
        HQ_ADDRESS_ALT: (
            <>
                {altHqLangValues?.length ? (
                    <InputComponent
                        notRequired={true}
                        disabled={true}
                        label={
                            zadostTyp === ETypZadostiLei.FUND
                                ? 'LEI_DETAIL_ADRESA_HQ_FUND_VARIANTA'
                                : 'LEI_DETAIL_ADRESA_HQ_VARIANTA'
                        }
                        name="hqlangfirstaddresslineP"
                        initialValue={`${
                            altHqLangValues &&
                            altHqLangValues[0].firstaddressline &&
                            altHqLangValues[0].firstaddressline
                        }, ${
                            altHqLangValues &&
                            altHqLangValues[0].additionaladdressline &&
                            altHqLangValues[0].additionaladdressline
                        },  ${altHqLangValues && altHqLangValues[0].city}, ${
                            altHqLangValues && altHqLangValues[0].postalcode
                        }, ${altHqLangValues && getCountryText(altHqLangValues[0].country || '', lang)} ${
                            altHqLangValues && !!altHqLangValues[0].region
                                ? ', ' +
                                  getRegionText(altHqLangValues[0].country || '', altHqLangValues[0].region || '', lang)
                                : ''
                        }`}
                        maxLength={100}
                    />
                ) : null}
            </>
        )
    };
};

const DetailInfo: React.FC<IDetailInfo> = ({ formDataSpolecnost, typZadosti, rejstrikErrs }) => {
    const [form] = Form.useForm();
    const { language } = useContext(Context);
    const setForceRerender = useState<boolean>(false)[1];
    const rerender = () => {
        setForceRerender(val => !val);
    };
    useEffect(() => {
        setTimeout(() => rerender(), 0); // rerender po nastaveni default values (inak sa niektore nezobrazia)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prehledInputs = getPrehledFields(formDataSpolecnost, language, typZadosti, rejstrikErrs);

    return (
        <FormWrapper outForm={form} onFinish={() => {}} layoutPosition="horizontal">
            <SelectComponent
                isFormItem={true}
                disabled={true}
                name="jurisdikce"
                notRequired={true}
                defaultValue={formDataSpolecnost?.jurisdikce}
                labelParagraph="LEI_DETAIL_JURISDICKE"
                data={
                    typZadosti === ETypZadostiLei.FUND
                        ? leiCountrySelectFondy
                        : typZadosti === ETypZadostiLei.INTERNATIONAL_ORGANIZATION
                          ? leiCountrySelectInternational
                          : leiCountrySelect
                }
            />
            <SelectComponent
                notRequired={true}
                disabled={true}
                isFormItem={true}
                name="registrationauthorityid"
                showOptionValue={true}
                optionValueFilter={['RA888888']}
                defaultValue={formDataSpolecnost?.registrationauthorityid}
                labelParagraph="REJSTRIK_KOD"
                data={[
                    ...(form.getFieldValue('country')
                        ? _getKeyValue_(form.getFieldValue('country'))(leiRejstrikySelect)
                        : []),
                    ...leiRejstrikyPermanent
                ]}
            />
            {formDataSpolecnost?.registrationauthorityentityid && (
                <InputComponent
                    notRequired={true}
                    disabled={true}
                    label="LEI_ICO"
                    name="registrationauthorityentityid"
                    initialValue={formDataSpolecnost?.registrationauthorityentityid}
                    maxLength={30}
                />
            )}
            {prehledInputs.LEGAL_NAME}
            {prehledInputs.OTHER_NAMES}
            {/* formDataSpolecnost?.transliteratedotherentitynames && (
                <InputComponent
                    notRequired={true}
                    disabled={true}
                    label="LEI_DETAIL_JINY_TRANS"
                    name="transliteratedotherentitynames"
                    initialValue={formDataSpolecnost?.transliteratedotherentitynames}
                    maxLength={100}
                />
            )
  

             <SelectComponent
                notRequired={true}
                disabled={true}
                name="entitystatusdetail"
                labelParagraph="LEI_DETAIL_STATUS"
                isFormItem={true}
                data={leiStatusSelect}
                defaultValue={formDataSpolecnost?.entitystatus}
            />*/}
            {prehledInputs.LEGAL_FORM}
            {prehledInputs.VZNIKSPOLECNOSTI}
            {prehledInputs.LEGAL_ADDRESS}
            {prehledInputs.HQ_ADDRESS}
            {prehledInputs.LEGAL_ADDRESS_ALT}
            {prehledInputs.HQ_ADDRESS_ALT}
        </FormWrapper>
    );
};

export default DetailInfo;
